/**
 * Brand color configuration
 * Centralized color definitions for the entire application
 */

export const brandColors = {
  // Primary brand colors
  primary: {
    base: "#3F3227", // Main primary color - RGB(63, 50, 39)
    hover: "#2F2419", // Hover state (darker)
    active: "#1F1810", // Active/pressed state (darkest)
  },

  // Secondary brand colors
  secondary: {
    base: "#DAD7D0", // Main secondary color - RGB(218, 215, 208)
    hover: "#CAC7C0", // Hover state (slightly darker)
    active: "#BAB7B0", // Active/pressed state (darker)
  },

  // Form colors
  form: {
    background: "#FFFFFF", // Form block background - always white
    border: "#E5E7EB", // Form border color (gray-200)
  },

  // Gradient colors for background
  gradient: {
    from: "#3F3227", // Gradient start - RGB(63, 50, 39)
    to: "#2F2419", // Gradient end (darker)
    overlay: {
      from: "#3F3227", // Overlay gradient start (with opacity)
      to: "#2F2419", // Overlay gradient end (with opacity)
    },
  },

  // Semantic colors (can be overridden if needed)
  semantic: {
    success: {
      bg: "bg-green-100",
      text: "text-green-600",
      border: "border-green-500",
    },
    error: {
      bg: "bg-red-50",
      text: "text-red-600",
      border: "border-red-500",
      ring: "ring-red-500",
    },
    warning: {
      bg: "bg-amber-50",
      border: "border-amber-200",
    },
    info: {
      bg: "bg-blue-50",
      border: "border-blue-200",
      text: "text-blue-900",
    },
  },

  // Neutral colors
  neutral: {
    gray: {
      50: "bg-gray-50",
      100: "bg-gray-100",
      200: "border-gray-200",
      300: "border-gray-300",
      400: "bg-gray-400",
      500: "text-gray-500",
      600: "text-gray-600",
      700: "text-gray-700",
      900: "text-gray-900",
    },
  },
};

/**
 * Helper function to get Tailwind color classes
 * Usage: getColorClass('primary.base') => 'bg-[#3F3227]'
 */
export const getColorClass = (colorPath, type = "bg") => {
  const keys = colorPath.split(".");
  let value = brandColors;

  for (const key of keys) {
    value = value[key];
    if (!value) return "";
  }

  if (typeof value === "string" && value.startsWith("#")) {
    return `${type}-[${value}]`;
  }

  return value;
};

/**
 * Helper function to get inline style color
 * Usage: getColorStyle('primary.base') => { backgroundColor: '#3F3227' }
 */
export const getColorStyle = (colorPath, property = "backgroundColor") => {
  const keys = colorPath.split(".");
  let value = brandColors;

  for (const key of keys) {
    value = value[key];
    if (!value) return {};
  }

  if (typeof value === "string" && value.startsWith("#")) {
    return { [property]: value };
  }

  return {};
};

// Legacy export for backward compatibility (can be removed later)
export const loginColors = brandColors;
